import React, { FC, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthUserContext } from '@yakoffice/yakoffice-firebase';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import { useItem, useItemSearchParameters } from '../use-item';
import { DataObjectType, ObjectProperty, UrlLinkProperty } from '../../model/data';
import { CreateDataSourceRoute, CreateItemRoute } from '../../routes/PlayersRoutes';
import { YoGameEnvironmentRoles } from '../../config/yo-game-environment-roles';
import { PlayersHeader } from '../players-header/players-header';
import styles from './dataSource.module.sass';
import { useDataSource } from '../use-data-sources';
import { DocumentTitle, NoAccess, ProjectName } from '@yakoffice/shared-components';
import { observer } from 'mobx-react-lite';
import { PlayerActions } from '../player-actions/player-actions';
import { DataSourceRender } from './dataSourceRender';


export const ReRenderItemContext = React.createContext({} as () => void);

export const DataSource: FC = observer(() => {

    const authUser = useContext(AuthUserContext);
    const navigate = useNavigate();
    const { projectId, gameEnvironmentId, itemId } = useParams() as {
        projectId: string,
        gameEnvironmentId: string,
        itemId: string
    };
    const dataSource = useDataSource();
    const [searchParameters, setSearchParameters] = useState<useItemSearchParameters>({ itemId: '' });
    const itemResult = useItem(searchParameters);

    useEffect(() => {
        if (itemId) {
            setSearchParameters({ itemId: itemId });
            DocumentTitle(itemId + ` - ${gameEnvironmentId} - Players - ` + ProjectName(projectId));
        }
    }, [itemId, gameEnvironmentId, projectId, setSearchParameters]);

    const handleGameEnvironmentChange = (gameEnvironmentId: string) => {
        setSearchParameters({ itemId: '' });
        navigate(CreateDataSourceRoute(projectId, gameEnvironmentId.toLowerCase(), dataSource?.id));
    };

    const handleSearchItem = (itemId: string) => {
        setSearchParameters({ itemId: itemId });
        navigate(CreateItemRoute(projectId, gameEnvironmentId, dataSource?.id, itemId));
    };

    const reRenderItem = () => {
        setSearchParameters({ itemId: searchParameters.itemId });
    };

    const getPlayerDbUrl = () => {
        const playerObject = itemResult.item as ObjectProperty;
        return playerObject.value.find(value => (value.label.toLowerCase() === 'view' && value.type === DataObjectType.urlLink)) as UrlLinkProperty;
    };

    return (authUser?.hasGameEnvironmentRoleClaim(projectId, gameEnvironmentId, YoGameEnvironmentRoles.ViewPlayers)
            ?
            <div id="datasource" className={`${styles.projectContent} projectContent themed-project`}>
                <PlayersHeader handleGameEnvironmentChange={handleGameEnvironmentChange}
                               handleSearchItem={handleSearchItem}
                               title={'Players'} />

                {authUser && itemResult.isLoaded && itemResult.item &&
                    <PlayerActions authUser={authUser} projectId={projectId}
                                   gameEnvironmentId={gameEnvironmentId}
                                   itemId={itemId} itemResult={itemResult} reRenderItem={reRenderItem}
                                   playerProperty={getPlayerDbUrl()}
                    />
                }


                {itemResult.isLoading &&
                    <CustomSpinner spinnerText={`Searching for ${dataSource?.name}...`} />
                }
                {(itemResult.isLoaded && !itemResult.item) &&
                    <Container fluid>
                        <Alert className="mt-4 status-alert" data-testid="alertNotFound">
                            <h3 className="d-block text-lg">Player not
                                found</h3>
                            <i className="fas fa-user-times" />
                            Could not find a player with ID: {searchParameters.itemId}
                        </Alert>
                    </Container>
                }
                {(itemResult.isLoaded && itemResult.item !== null) &&
                    <DataSourceRender dataObject={itemResult.item as ObjectProperty} reRenderItem={reRenderItem} />
                }

            </div>
            : <NoAccess />
    );
});
